import { getEnabledElement, eventTarget, Enums } from '@cornerstonejs/core';
import type { Types } from '@cornerstonejs/core';
import { vec3, vec2 } from 'gl-matrix';
import { Events, ToolModes, StrategyCallbacks } from '../../enums';

import type {
  PublicToolProps,
  ToolProps,
  EventTypes,
  SVGDrawingHelper,
  LabelmapToolOperationDataVolume,
  LabelmapToolOperationDataStack,
} from '../../types';
import {
  fillInsideSphere,
  thresholdInsideSphere,
  thresholdInsideSphereIsland,
} from './strategies/fillSphere';
import { eraseInsideSphere } from './strategies/eraseSphere';
import {
  thresholdInsideCircle,
  fillInsideCircle,
} from './strategies/fillCircle';
import { eraseInsideCircle } from './strategies/eraseCircle';
import {
  resetElementCursor,
  hideElementCursor,
} from '../../cursors/elementCursor';

import triggerAnnotationRenderForViewportUIDs from '../../utilities/triggerAnnotationRenderForViewportIds';
import LabelmapBaseTool from './LabelmapBaseTool';
import { getStrategyData } from './strategies/utils/getStrategyData';
import LazyBrushEditController from './utils/LazyBrushEditController';
import { shouldUseLazyLabelmapEditing } from './utils/shouldUseLazyLabelmapEditing';
import { getActiveSegmentation } from '../../stateManagement/segmentation/getActiveSegmentation';

/**
 * @public
 */
class BrushTool extends LabelmapBaseTool {
  static toolName;
  // Remember the last drag position in both canvas and world space so we can
  // pass a full stroke segment to the strategies instead of a single point.
  private _lastDragInfo: {
    canvas: Types.Point2;
    world: Types.Point3;
  } | null = null;
  // Native-event timestamp of the interaction that activated the current draw
  // loop, used to discard delayed TOUCH_TAP events from a previous gesture.
  private _drawStartTimeStamp = 0;
  private _lazyEdit = new LazyBrushEditController();

  constructor(
    toolProps: PublicToolProps = {},
    defaultToolProps: ToolProps = {
      supportedInteractionTypes: ['Mouse', 'Touch'],
      configuration: {
        strategies: {
          /** Perform fill of the active segment index inside a (2d) circle */
          FILL_INSIDE_CIRCLE: fillInsideCircle,
          /** Erase (to 0) inside a circle */
          ERASE_INSIDE_CIRCLE: eraseInsideCircle,
          /** Fill a 3d sphere with the active segment index */
          FILL_INSIDE_SPHERE: fillInsideSphere,
          /** Erase inside a 3d sphere, clearing any segment index (to 0) */
          ERASE_INSIDE_SPHERE: eraseInsideSphere,
          /**
           * Threshold inside a circle, either with a dynamic threshold value
           * based on the voxels in a 2d plane around the center click.
           * Performs island removal.
           */
          THRESHOLD_INSIDE_CIRCLE: thresholdInsideCircle,
          /**
           * Threshold inside a sphere, either dynamic or pre-configured.
           * For dynamic, base the threshold on a 2d CIRCLE around the center click.
           * Do not perform island removal (this may be slow)
           * Users may see delays dragging the sphere for large radius values and
           * for complex mixtures of texture.
           */
          THRESHOLD_INSIDE_SPHERE: thresholdInsideSphere,
          /**
           * Threshold inside a sphere, but also include island removal.
           * The current implementation of this is fairly fast now, but users may
           * see delays when island removal occurs on large sections of the volume.
           */
          THRESHOLD_INSIDE_SPHERE_WITH_ISLAND_REMOVAL:
            thresholdInsideSphereIsland,
        },
        defaultStrategy: 'FILL_INSIDE_CIRCLE',
        activeStrategy: 'FILL_INSIDE_CIRCLE',
        brushSize: 25,
        useCenterSegmentIndex: false,
        preview: {
          // Have to enable the preview to use this
          enabled: false,
          previewColors: {
            0: [255, 255, 255, 128],
          },
          // The time before showing a preview
          previewTimeMs: 250,
          // The distance to move to show a preview before preview time expired
          previewMoveDistance: 8,
          // The distance to drag before being considered a drag rather than click
          dragMoveDistance: 4,
          // The time to consider a mouse click a drag when moved less than dragMoveDistance
          dragTimeMs: 500,
        },
        actions: {
          [StrategyCallbacks.AcceptPreview]: {
            method: StrategyCallbacks.AcceptPreview,
            bindings: [
              {
                key: 'Enter',
              },
            ],
          },
          [StrategyCallbacks.RejectPreview]: {
            method: StrategyCallbacks.RejectPreview,
            bindings: [
              {
                key: 'Escape',
              },
            ],
          },
          /**
           * Pressing the i key will interpolate between labelmap slices,
           * without distance or extent extrapolation.  This has to be used
           * on overlapping segments.  That is, for two slices (axial, sagittal or coronal),
           * with intervening slices, where the segments occupy the same area
           * of the image, an interpolation between the two segments will
           * created on intervening slices.
           */
          [StrategyCallbacks.Interpolate]: {
            method: StrategyCallbacks.Interpolate,
            bindings: [
              {
                key: 'i',
              },
            ],
            configuration: {
              useBallStructuringElement: true,
              // noHeuristicAlignment: true,
              noUseDistanceTransform: true,
              noUseExtrapolation: true,
            },
          },
          /**
           * Pressing the e key will interpolate with the full set of defaults,
           * that includes some distance/extent extrapolation, so can interpolate
           * between SLIGHTLY non-overlapping labelmaps.  That is, if you have
           * segments on two slices, and the slices occupy almost the same
           * are on the two different slices, then the slices in between will
           * be interpolated.  If the position is quite different, it may create
           * an interpolation, but it won't be a very useful one as it will be
           * two separate interpolations going to an empty space.
           */
          interpolateExtrapolation: {
            method: StrategyCallbacks.Interpolate,
            bindings: [
              {
                key: 'e',
              },
            ],
            // Morphological interpolation config
            configuration: {},
          },
        },
      },
    }
  ) {
    super(toolProps, defaultToolProps);
  }

  onSetToolPassive = (_evt) => {
    this.disableCursor();
  };

  onSetToolEnabled = () => {
    this.disableCursor();
  };

  onSetToolDisabled = (_evt) => {
    this.disableCursor();
  };

  private disableCursor() {
    this._clearPendingLazyPreviewCleanup();
    this._hoverData = undefined;
    this._resetLazyEditState();
    this.rejectPreview();
  }

  private _isLazyLabelmapEditingEnabled(viewport?: Types.IViewport): boolean {
    return shouldUseLazyLabelmapEditing(viewport ?? this._hoverData?.viewport);
  }

  private _resetLazyEditState(): void {
    this._lazyEdit.reset();
  }

  private _clearPendingLazyPreviewCleanup(): void {
    this._lazyEdit.clearPendingCleanup();
  }

  private _refreshCursor(
    element: HTMLDivElement,
    centerCanvas: Types.Point2
  ): void {
    this._hoverData = this.createHoverData(element, centerCanvas);
    if (!this._hoverData) {
      return;
    }

    this._calculateCursor(element, centerCanvas);

    BrushTool.activeCursorTool = this;
    triggerAnnotationRenderForViewportUIDs(this._hoverData.viewportIdsToRender);
  }

  private _scheduleLazyPreviewCleanup(
    element: HTMLDivElement,
    centerCanvas: Types.Point2,
    viewportId: string,
    segmentationId: string,
    isTouch = false
  ): void {
    this._lazyEdit.scheduleCleanup({
      element,
      centerCanvas,
      viewportId,
      segmentationId,
      refreshCursor: isTouch
        ? (cleanupElement: HTMLDivElement) => this._clearCursor(cleanupElement)
        : this._refreshCursor.bind(this),
    });
  }

  private _captureLazyPreviewCircle(): void {
    if (!this._isLazyLabelmapEditingEnabled() || !this._hoverData) {
      return;
    }

    this._lazyEdit.capturePreviewCircle(this._hoverData);
  }

  preMouseDownCallback = (
    evt: EventTypes.MouseDownActivateEventType
  ): boolean => {
    const eventData = evt.detail;
    const { element, currentPoints } = eventData;
    const enabledElement = getEnabledElement(element);
    const { viewport } = enabledElement;

    const activeSegmentation = getActiveSegmentation(viewport.id);
    if (!activeSegmentation) {
      const event = new CustomEvent(Enums.Events.ERROR_EVENT, {
        detail: {
          type: 'Segmentation',
          message:
            'No active segmentation detected, create a segmentation representation before using the brush tool',
        },
        cancelable: true,
      });
      eventTarget.dispatchEvent(event);
      return false;
    }

    // @ts-expect-error
    this._editData = this.createEditData(element);

    // This might be a mouse down
    this._previewData.isDrag = false;
    this._previewData.timerStart = Date.now();
    this._drawStartTimeStamp = (eventData.event as Event)?.timeStamp ?? 0;
    const canvasPoint = vec2.clone(currentPoints.canvas) as Types.Point2;
    const worldPoint = viewport.canvasToWorld([
      canvasPoint[0],
      canvasPoint[1],
    ]) as Types.Point3;
    this._lastDragInfo = {
      canvas: canvasPoint,
      world: vec3.clone(worldPoint) as Types.Point3,
    };

    this._hoverData = this.createHoverData(element, canvasPoint);
    if (!this._hoverData) {
      // Transient state (e.g. no active segment index yet, or the viewport's
      // camera is not resolvable) - clear the edit data assigned above and bail
      // before the draw listeners are bound below.
      this._editData = null;
      return false;
    }
    this._calculateCursor(element, canvasPoint);
    BrushTool.activeCursorTool = this;
    this._resetLazyEditState();

    if (this._isLazyLabelmapEditingEnabled(this._hoverData.viewport)) {
      this._lazyEdit.appendStrokePoint(worldPoint);
      this._captureLazyPreviewCircle();
    }

    triggerAnnotationRenderForViewportUIDs(this._hoverData.viewportIdsToRender);

    const operationData = this.getOperationData(element);
    if (!operationData) {
      return false;
    }

    // Bind the draw listeners and hide the cursor only once every guard above
    // has passed — a `return false` after these would leave the cursor hidden
    // and the drag listeners attached with no draw in progress.
    this._activateDraw(element);
    hideElementCursor(element);
    evt.preventDefault();

    this.applyActiveStrategyCallback(
      enabledElement,
      operationData,
      StrategyCallbacks.OnInteractionStart
    );

    return true;
  };

  /**
   * This call will be made when the mouse moves and the tool is active, but
   * not actually drawing at the moment.
   * The behavior is:
   *    1. Update the cursor
   *    2. Call the active strategy event 'preview' and 'rejectPreview'
   *       on the mouse cursor position on a periodic basis to create a preview
   *       when configured to do so.
   *
   * The preview will be shown after the mouse has been stationary for 250 ms.
   * Any preview will be cancelled (immediately) after moving outside the center
   * distance.
   * As well, if the mouse moves but stays inside the center area for 250 ms,
   * then the cancel will happen with a new preview being added.
   *
   * See mouse up details for how the preview gets accepted.
   *
   * The preview also needs to be cancelled on changing tools.
   */
  mouseMoveCallback = (evt: EventTypes.InteractionEventType): void => {
    if (!this.isPrimary) {
      return;
    }
    if (this.mode === ToolModes.Active) {
      this.updateCursor(evt);
      if (!this.configuration.preview.enabled) {
        return;
      }
      const { previewTimeMs, previewMoveDistance, dragMoveDistance } =
        this.configuration.preview;
      const { currentPoints, element } = evt.detail;
      const { canvas } = currentPoints;

      const { startPoint, timer, timerStart, isDrag } = this._previewData;

      if (isDrag) {
        return;
      }

      const delta = vec2.distance(canvas, startPoint);
      const time = Date.now() - timerStart;
      if (
        delta > previewMoveDistance ||
        (time > previewTimeMs && delta > dragMoveDistance)
      ) {
        if (timer) {
          window.clearTimeout(timer);
          this._previewData.timer = null;
        }
        if (!isDrag) {
          this.rejectPreview(element);
        }
      }
      if (!this._previewData.timer) {
        const timer = window.setTimeout(this.previewCallback, 250);
        Object.assign(this._previewData, {
          timerStart: Date.now(),
          timer,
          startPoint: canvas,
          element,
        });
      }
    }
  };

  previewCallback = () => {
    // Don't run the preview callback if we're currently dragging
    if (this._previewData.isDrag) {
      this._previewData.timer = null;
      return;
    }

    this._previewData.timer = null;

    // get strategy data
    const operationData = this.getOperationData(this._previewData.element) as
      | LabelmapToolOperationDataStack
      | LabelmapToolOperationDataVolume;
    if (!operationData) {
      return;
    }

    const enabledElement = getEnabledElement(this._previewData.element);
    if (!enabledElement) {
      return;
    }

    const { viewport } = enabledElement;
    // active strategy
    const activeStrategy = this.configuration.activeStrategy;
    const strategyData = getStrategyData({
      operationData,
      viewport,
      strategy: activeStrategy,
    });

    if (!operationData) {
      return;
    }

    const memo = this.createMemo(
      operationData.segmentationId,
      strategyData.segmentationVoxelManager
    );

    this._setPreview(
      this.applyActiveStrategyCallback(
        getEnabledElement(this._previewData.element),
        {
          ...operationData,
          ...strategyData,
          memo,
        },
        StrategyCallbacks.Preview
      )
    );
  };

  /**
   * Updates the cursor position and whether it is showing or not.
   * Can be over-ridden to add more cursor details or a preview.
   */
  protected updateCursor(evt: EventTypes.InteractionEventType) {
    const eventData = evt.detail;
    const { element } = eventData;
    const { currentPoints } = eventData;
    const centerCanvas = currentPoints.canvas;
    this._refreshCursor(element, centerCanvas);
  }

  private _clearCursor(element: HTMLDivElement): void {
    // The cursor may have been rendered on linked viewports too; rerender
    // all of them, not just the source viewport, so no stale circle remains.
    const viewportIdsToRender = this._hoverData?.viewportIdsToRender;
    this._hoverData = undefined;

    if (viewportIdsToRender?.length) {
      triggerAnnotationRenderForViewportUIDs(viewportIdsToRender);
      return;
    }

    const enabledElement = getEnabledElement(element);
    if (enabledElement) {
      triggerAnnotationRenderForViewportUIDs([enabledElement.viewport.id]);
    }
  }

  private _dragCallback = (evt: EventTypes.InteractionEventType): void => {
    const eventData = evt.detail;
    const { element, currentPoints } = eventData;

    const { currentPointsList } = eventData as EventTypes.TouchDragEventDetail;
    if (currentPointsList?.length > 1) {
      // A second finger reclassifies the gesture (pinch zoom, multi-finger
      // scroll); stop painting and roll the stroke back rather than keep
      // drawing at the mean touch point.
      this._cancelTouchDraw(element);
      return;
    }

    const enabledElement = getEnabledElement(element);
    const { viewport } = enabledElement;

    this.updateCursor(evt);

    if (!this._hoverData) {
      return;
    }

    const { viewportIdsToRender } = this._hoverData;

    triggerAnnotationRenderForViewportUIDs(viewportIdsToRender);

    const delta = vec2.distance(
      currentPoints.canvas,
      this._previewData.startPoint
    );
    const { dragTimeMs, dragMoveDistance } = this.configuration.preview;
    if (
      !this._previewData.isDrag &&
      Date.now() - this._previewData.timerStart < dragTimeMs &&
      delta < dragMoveDistance
    ) {
      // If we are showing a preview, then don't start dragging quite immediately
      // so that click up can accept the preview.
      return;
    }

    // Clear any pending preview timer when dragging starts
    if (this._previewData.timer) {
      window.clearTimeout(this._previewData.timer);
      this._previewData.timer = null;
    }
    if (!this._lastDragInfo) {
      const startCanvas = this._previewData.startPoint;
      const startWorld = viewport.canvasToWorld([
        startCanvas[0],
        startCanvas[1],
      ]) as Types.Point3;
      this._lastDragInfo = {
        canvas: vec2.clone(startCanvas) as Types.Point2,
        world: vec3.clone(startWorld) as Types.Point3,
      };
    }

    const currentCanvas = currentPoints.canvas;
    const currentWorld = viewport.canvasToWorld([
      currentCanvas[0],
      currentCanvas[1],
    ]) as Types.Point3;

    this._hoverData = this.createHoverData(element, currentCanvas);
    if (!this._hoverData) {
      return;
    }

    this._calculateCursor(element, currentCanvas);

    if (this._isLazyLabelmapEditingEnabled(this._hoverData.viewport)) {
      this._lazyEdit.appendStrokePoint(currentWorld);
      // `capturePreviewCircle` only accumulates the cursor points, so the drag
      // writes no voxels. The lazy stroke paints the whole path once, in
      // `_endCallback`, and that call stores the preview.
      this._captureLazyPreviewCircle();
      this._previewData.preview = null;
    } else {
      const operationData = this.getOperationData(element);
      if (!operationData) {
        return;
      }
      // Hand the strategy the exact stroke segment we just traversed so it can
      // paint a continuous capsule in one pass instead of trying to infer the
      // path from scattered samples.
      operationData.strokePointsWorld = [
        vec3.clone(this._lastDragInfo.world) as Types.Point3,
        vec3.clone(currentWorld) as Types.Point3,
      ];

      this._setPreview(this.applyActiveStrategy(enabledElement, operationData));
    }

    const currentCanvasClone = vec2.clone(currentCanvas) as Types.Point2;
    this._lastDragInfo = {
      canvas: currentCanvasClone,
      world: vec3.clone(currentWorld) as Types.Point3,
    };
    this._previewData.element = element;
    // Add a bit of time to the timer start so small accidental movements dont
    // cause issues on clicking
    this._previewData.timerStart = Date.now() + dragTimeMs;
    this._previewData.isDrag = true;
    this._previewData.startPoint = currentCanvasClone;
  };

  /**
   * Cancels an in-progress touch stroke: rolls back the voxel writes recorded
   * so far and tears the draw loop down, so a gesture that turns out to be
   * multi-finger does not leave paint behind. The memo is committed only to
   * materialize its undo state and is discarded without reaching the history
   * stack.
   *
   * Overrides the base teardown rather than extending it: a brush stroke has
   * already written to the labelmap, and the preview/lazy-edit state has no
   * counterpart on the scissors tools.
   */
  protected override _cancelTouchDraw(element: HTMLDivElement): void {
    const { memo } = this;
    if (memo?.commitMemo?.()) {
      memo.restoreMemo(true);
    }
    this.memo = null;

    this._deactivateDraw(element);
    resetElementCursor(element);

    this._editData = null;
    this._lastDragInfo = null;
    this._previewData.preview = null;
    this._previewData.isDrag = false;
    if (this._previewData.timer) {
      window.clearTimeout(this._previewData.timer);
      this._previewData.timer = null;
    }
    this._resetLazyEditState();
    this._clearCursor(element);
  }

  private _calculateCursor(element, _centerCanvas) {
    const enabledElement = getEnabledElement(element);
    const operationData = this.getOperationData(element);

    if (!enabledElement || !operationData) {
      return;
    }

    this.applyActiveStrategyCallback(
      enabledElement,
      operationData,
      StrategyCallbacks.CalculateCursorGeometry
    );
  }

  /**
   * The end callback call is made when the mouse is released.  This will
   * perform another active strategy render event to render the final position.
   * As well, the finish strategy callback will be made during this time.
   */
  private _endCallback = (evt: EventTypes.InteractionEventType): void => {
    const eventData = evt.detail;
    const { element } = eventData;

    if (eventData.eventName === Events.TOUCH_TAP) {
      // TOUCH_TAP is dispatched on a delay after touchend, so a tap from a
      // previous gesture can land while a new stroke is in progress. Only
      // honor taps belonging to the gesture that activated this draw loop.
      const tapTimeStamp = (eventData.event as Event)?.timeStamp ?? 0;
      if (tapTimeStamp < this._drawStartTimeStamp) {
        return;
      }
    }

    const enabledElement = getEnabledElement(element);

    const operationData = this.getOperationData(element);
    if (!operationData) {
      return;
    }
    const isLazyLabelmapEditing = this._isLazyLabelmapEditingEnabled(
      this._hoverData?.viewport
    );

    if (isLazyLabelmapEditing && this._previewData.isDrag) {
      operationData.strokePointsWorld = this._lazyEdit
        .getStrokePointsWorld()
        .map((point) => vec3.clone(point) as Types.Point3);
      // The lazy stroke paints the whole path here, so this is the call that
      // creates the preview. Store the result, or a later reject finds no preview
      // and leaves the stroke on the labelmap.
      this._setPreview(this.applyActiveStrategy(enabledElement, operationData));
    } else if (!this._previewData.preview && !this._previewData.isDrag) {
      // Don't re-fill when the preview is showing and the user clicks again
      // otherwise the new area of hover may get filled, which is unexpected
      this._setPreview(this.applyActiveStrategy(enabledElement, operationData));
    }

    this.doneEditMemo();

    this._deactivateDraw(element);

    resetElementCursor(element);

    this._editData = null;

    this._lastDragInfo = null;

    if (isLazyLabelmapEditing && this._previewData.isDrag) {
      this._scheduleLazyPreviewCleanup(
        element,
        evt.detail.currentPoints.canvas as Types.Point2,
        enabledElement.viewport.id,
        operationData.segmentationId,
        this._isTouchInteraction(evt)
      );
      triggerAnnotationRenderForViewportUIDs(
        this._hoverData.viewportIdsToRender
      );
    } else {
      this._resetLazyEditState();
      if (this._isTouchInteraction(evt)) {
        // On touch there is no pointer hovering after release, so clear the
        // cursor instead of re-showing it at the lift point.
        this._clearCursor(element);
      } else {
        this.updateCursor(evt);
      }
    }

    this.applyActiveStrategyCallback(
      enabledElement,
      operationData,
      StrategyCallbacks.OnInteractionEnd
    );

    if (!this._previewData.isDrag) {
      this.acceptPreview(element);
    }
  };

  public getStatistics(element, segmentIndices?) {
    if (!element) {
      return;
    }
    const enabledElement = getEnabledElement(element);
    const operationData = this.getOperationData(element);

    if (!enabledElement || !operationData) {
      return;
    }

    const stats = this.applyActiveStrategyCallback(
      enabledElement,
      operationData,
      StrategyCallbacks.GetStatistics,
      segmentIndices
    );

    return stats;
  }

  /**
   * Cancels any preview view being shown, resetting any segments being shown.
   */
  public rejectPreview(element = this._previewData.element) {
    if (!element) {
      return;
    }

    this.doneEditMemo();

    const enabledElement = getEnabledElement(element);
    if (!enabledElement) {
      return;
    }
    const operationData = this.getOperationData(element);
    if (!operationData) {
      return;
    }

    // `previewData` is a static shared by every labelmap tool, so an element being set
    // says only that some tool has painted — not that a preview is on the labelmap.
    // `preview` answers that second question, because every paint path stores its result
    // through `_setPreview`, which keeps a result only when the strategy set a preview up.
    //
    // Running the strategy without a preview asks for a full strategy initialization to
    // do nothing: the reject handler itself undoes only a memo that carries preview
    // voxels. The 3D variants pay for it with a throw —
    // `ensureSegmentationVolumeFor3DManipulation` raises `Volume is not reconstructable
    // for sphere manipulation` on a viewport that cannot form a volume — and deactivating
    // a sphere brush is enough to reach it, since `onSetToolPassive` rejects the preview.
    // That throw escapes `ToolGroup.setToolPassive` and leaves the tool group with no
    // active tool.
    if (this._previewData.preview) {
      this.applyActiveStrategyCallback(
        enabledElement,
        operationData,
        StrategyCallbacks.RejectPreview
      );
    }

    this._previewData.preview = null;
    this._previewData.isDrag = false;
  }

  /**
   * Accepts a preview, marking it as the active segment.
   */
  public acceptPreview(element = this._previewData.element) {
    if (!element) {
      return;
    }

    // Call parent method to handle memo tracking
    super.acceptPreview(element);
  }

  /**
   * Performs interpolation on the active segment index
   */
  public interpolate(element, config) {
    if (!element) {
      return;
    }
    const enabledElement = getEnabledElement(element);
    const operationData = this.getOperationData(element);

    if (!enabledElement || !operationData) {
      return;
    }

    this._setPreview(
      this.applyActiveStrategyCallback(
        enabledElement,
        operationData,
        StrategyCallbacks.Interpolate,
        config.configuration
      )
    );
    this._previewData.isDrag = true;
  }

  /**
   * Add event handlers for the modify event loop, and prevent default event propagation.
   */
  private _activateDraw = (element: HTMLDivElement): void => {
    element.addEventListener(
      Events.MOUSE_UP,
      this._endCallback as EventListener
    );
    element.addEventListener(
      Events.MOUSE_DRAG,
      this._dragCallback as EventListener
    );
    element.addEventListener(
      Events.MOUSE_CLICK,
      this._endCallback as EventListener
    );

    element.addEventListener(
      Events.TOUCH_END,
      this._endCallback as EventListener
    );
    element.addEventListener(
      Events.TOUCH_DRAG,
      this._dragCallback as EventListener
    );
    element.addEventListener(
      Events.TOUCH_TAP,
      this._endCallback as EventListener
    );
  };

  /**
   * Add event handlers for the modify event loop, and prevent default event prapogation.
   */
  private _deactivateDraw = (element: HTMLDivElement): void => {
    element.removeEventListener(
      Events.MOUSE_UP,
      this._endCallback as EventListener
    );
    element.removeEventListener(
      Events.MOUSE_DRAG,
      this._dragCallback as EventListener
    );
    element.removeEventListener(
      Events.MOUSE_CLICK,
      this._endCallback as EventListener
    );

    element.removeEventListener(
      Events.TOUCH_END,
      this._endCallback as EventListener
    );
    element.removeEventListener(
      Events.TOUCH_DRAG,
      this._dragCallback as EventListener
    );
    element.removeEventListener(
      Events.TOUCH_TAP,
      this._endCallback as EventListener
    );
  };

  public invalidateBrushCursor() {
    if (this._hoverData === undefined) {
      return;
    }
    const { data } = this._hoverData.brushCursor;
    const { viewport } = this._hoverData;

    data.invalidated = true;

    // Todo: figure out if other brush metadata (other than segment color) should get updated
    // during the brush cursor invalidation
    const { segmentColor } = this.getActiveSegmentationData(viewport) || {};
    this._hoverData.brushCursor.metadata.segmentColor = segmentColor;
  }

  renderAnnotation(
    enabledElement: Types.IEnabledElement,
    svgDrawingHelper: SVGDrawingHelper
  ): void {
    if (!this._hoverData || BrushTool.activeCursorTool !== this) {
      return;
    }

    const { viewport } = enabledElement;

    const viewportIdsToRender = this._hoverData.viewportIdsToRender;

    if (!viewportIdsToRender.includes(viewport.id)) {
      return;
    }

    const brushCursor = this._hoverData.brushCursor;

    if (brushCursor.data.invalidated === true) {
      const { centerCanvas } = this._hoverData;
      const { element } = viewport;

      // This can be set true when changing the brush size programmatically
      // whilst the cursor is being rendered.
      this._calculateCursor(element, centerCanvas);
    }

    this.applyActiveStrategyCallback(
      enabledElement,
      this.getOperationData(viewport.element),
      StrategyCallbacks.RenderCursor,
      svgDrawingHelper
    );
  }
}

BrushTool.toolName = 'Brush';
export default BrushTool;
